import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink } from "solid-icons/tb";
import { createMemo, For, Match, Show, Switch } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { GoogleIcon } from "./googleIcon";
import { type GoogleSetupRequiredPayload, parseGoogleSetupRequiredPayload } from "./googleSetupRequiredSchema";

interface GoogleSetupRequiredRendererProps {
  block: StreamingBlock;
}

const REASON_BODY_KEYS: Record<GoogleSetupRequiredPayload["reason"], string> = {
  workspace_not_configured: "google_setup_card.body_workspace_not_configured",
  not_exposed: "google_setup_card.body_not_exposed",
  domain_mismatch: "google_setup_card.body_domain_mismatch",
  scope_not_authorized: "google_setup_card.body_scope_not_authorized",
};

// The backend swaps this in for a Google result when a restricted capability is
// requested but the org's Workspace Service Account integration can't serve it.
// Org owners get a link to the integration settings; everyone else is told to
// ask their admin.
export const GoogleSetupRequiredRenderer = (props: GoogleSetupRequiredRendererProps) => {
  const rt = useChatRuntime();

  const isOrgAdmin = () => rt.ui?.isOrgAdmin?.() ?? false;
  const settingsHref = () => rt.actions.integrationSettingsHref?.("google");

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GoogleSetupRequiredPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGoogleSetupRequiredPayload(json);
  });

  return (
    <Show
      when={payload()}
      fallback={
        <Show when={rawContent()}>
          <div class="rounded-lg border border-primary-border bg-panel p-4">
            <p class="text-xs text-muted-text">{rt.t("google_setup_card.parse_error")}</p>
          </div>
        </Show>
      }
    >
      {(data) => (
        <div class="rounded-lg border border-primary-border bg-panel p-4">
          <div class="flex items-start gap-3">
            <GoogleIcon class="size-6 shrink-0" />
            <div class="min-w-0 flex-1">
              <h3 class="text-sm font-medium text-primary-text">{rt.t("google_setup_card.title")}</h3>
              <p class="mt-1 text-xs text-muted-text">{rt.t(REASON_BODY_KEYS[data().reason])}</p>
              <Show when={data().missingScopes?.length}>
                <div class="mt-2 flex flex-wrap items-center gap-1">
                  <span class="text-xs text-muted-text">{rt.t("google_setup_card.missing_scopes_label")}</span>
                  <For each={data().missingScopes}>
                    {(scope) => (
                      <code class="rounded bg-inset px-1 font-mono text-[0.7rem] text-primary-text">
                        {scope.replace("https://www.googleapis.com/auth/", "")}
                      </code>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </div>
          <Switch fallback={<p class="mt-3 text-xs text-muted-text">{rt.t("google_setup_card.non_admin_note")}</p>}>
            <Match when={isOrgAdmin() && settingsHref()}>
              <a
                href={settingsHref()}
                class="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover"
              >
                <span>{rt.t("google_setup_card.admin_cta")}</span>
                <TbExternalLink class="size-4" />
              </a>
            </Match>
            {/* Hosts that open settings out-of-process (chief → system browser)
                surface the same CTA as a button over the connectProvider seam. */}
            <Match when={rt.actions.connectProvider}>
              <button
                type="button"
                onClick={() => {
                  rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "google", via: "button" });
                  rt.actions.connectProvider?.("google");
                }}
                class="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover"
              >
                <span>{rt.t("google_setup_card.admin_cta")}</span>
                <TbExternalLink class="size-4" />
              </button>
            </Match>
          </Switch>
        </div>
      )}
    </Show>
  );
};
